import { CssBaseline, ThemeProvider } from "@mui/material";
import { AppTheme } from "./themes/AppTheme";
import { QueryClient, QueryClientProvider } from "react-query";
import { Fragment, lazy } from "react";
import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import LoginPage from "./components/auths/LoginPage";
import { UserProvider } from "./context/UserProvider";
import { UserAuth, isLoggedIn } from "./utils/util";
import { DialogProvider } from "./context/DialogContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AccountProvider } from "./context/AccountContext";
import { PhoneNumberProvider } from "./context/PhoneNumberContext";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PageLoadBoundary from "./widgets/PageLoadBoundary";

const Logout = lazy(() => import("./components/auths/LogOut"));
const HomeDash = lazy(() => import("./components/HomeDash"));
const TemplateView = lazy(() => import("./components/templates/TemplateView"));
const TrackCampaign = lazy(() => import("./components/campaign/TrackCampaign"));
const CampaignChats = lazy(() => import("./components/campaign/CampaignChats"));
const TemplateBuilder = lazy(() => import("./components/CreateTemplate"));
const UserRegistration = lazy(() => import("./components/profile/UserRegistration"));
const PaymentWidget = lazy(() => import("./components/payments/PaymentWidget"));
const ChatbotFlowBuilder = lazy(() => import("./components/autoreply/flows/ChatbotFlowBuilder"));
const ChatInterface = lazy(() => import("./ai/ChatInterface"));
const SelfOnBoarding = lazy(() => import("./components/SelfOnBoarding"));
const EmailUser = lazy(() => import("./components/containers/EmailUser"));
const SetCustomerPassword = lazy(() => import("./components/containers/SetCustomerPassword"));
const LinkPhoneNumber = lazy(() => import("./components/containers/LinkPhoneNumber"));
const PasswordRecovery = lazy(() => import("./components/auths/PasswordResetLink"));
const AccountLink = lazy(() => import("./components/accounts/AccountLink"));
const PricingPlansNew = lazy(() => import("./components/accounts/PricingPlansNew"));
const DownloadLeads = lazy(() => import("./components/accounts/DownloadLeads"));
const AccountExpiredCard = lazy(() => import("./components/accounts/AccountExpiredCard"));
const ContactsAnalysisDashboard = lazy(() => import("./components/database/analysis/DatabaseAnalysis"));
const ChatAiInterface = lazy(() => import("./ai/ChatAiInterface"));
const ImageSelectorExample = lazy(() => import("./widgets/ImageSelectorExample"));
const USeAiImage = lazy(() => import("./ai/USeAiImage"));
const LeadsReports = lazy(() => import("./components/pages/LeadsReports"));
const RechargeHub = lazy(() => import("./components/reseller/RechargeHub"));
const RechargeComponentReseller = lazy(() => import("./components/payments/RechargeComponentReseller"));
const GlobalRechargeComponent = lazy(() => import("./components/payments/GlobalRechargeComponent"));
const GOOGLE_CLIENT_ID =
  "427090715728-2o0ug2i644916bs1m59lbpv7vccaap9b.apps.googleusercontent.com";
const queryClient = new QueryClient();

const App = () => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider theme={AppTheme}>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DialogProvider>
              <UserProvider>
                <AccountProvider>
                  <PhoneNumberProvider>
                    <Fragment>
                      <CssBaseline />
                      <BrowserRouter>
                        <ToastContainer autoClose={1500} />
                        <PageLoadBoundary>
                          <Routes>
                            <Route
                              path="/"
                              element={<Navigate to="/home" replace />}
                            />
                            <Route
                              path="/login"
                              element={
                                isLoggedIn() ? (
                                  <Navigate to="/home" replace />
                                ) : (
                                  <LoginPage />
                                )
                              }
                            />
                            <Route path="/logout" element={<Logout />} />
                            <Route
                              path="/template-view/:id"
                              element={<TemplateView />}
                            />
                            <Route
                              path="/track-campaign"
                              element={<TrackCampaign />}
                            />
                            <Route
                              path="payment-widget"
                              element={<PaymentWidget />}
                            />
                            <Route path="chat-ai" element={<ChatInterface />} />
                            <Route
                              path="image-select"
                              element={<ImageSelectorExample />}
                            />

                            <Route
                              path="/account-expired"
                              element={<AccountExpiredCard />}
                            />
                            <Route
                              path="/database-analysis"
                              element={<ContactsAnalysisDashboard />}
                            />
                            <Route
                              path="/reseller-recharge/:id"
                              element={<GlobalRechargeComponent />}
                            />
                            <Route
                              path="/ai-interface"
                              element={<ChatAiInterface />}
                            />

                            <Route
                              path="/campaign-chats/:cusId/:campaignName"
                              element={<CampaignChats />}
                            />
                            <Route
                              path="/account-link/:wabaId/:phoneId"
                              element={<AccountLink />}
                            />
                            <Route
                              path="/pricing-plans"
                              element={<PricingPlansNew />}
                            />
                            <Route
                              path="/download-leads/:id"
                              element={<DownloadLeads />}
                            />
                            <Route path="/create-user" element={<EmailUser />} />
                            <Route
                              path="/set-password/:id"
                              element={<SetCustomerPassword />}
                            />
                            <Route
                              path="/link-phonenumber/:id"
                              element={<LinkPhoneNumber />}
                            />
                            <Route
                              path="/reset-password"
                              element={<PasswordRecovery />}
                            />
                            <Route
                              path="/:id/chatbot-flow-builder"
                              element={<ChatbotFlowBuilder />}
                            />
                            <Route
                              path="self-onboarding/:id"
                              element={<SelfOnBoarding />}
                            />
                            <Route
                              path=":id/get-leads"
                              element={<LeadsReports />}
                            />
                            <Route
                              path=":id/reseller"
                              element={<RechargeHub />}
                            />
                            <Route
                              path=":id/reseller-recharge"
                              element={<RechargeComponentReseller />}
                            />
                            <Route
                              path="/image-generate"
                              element={<USeAiImage />}
                            />
                            <Route
                              path="/register"
                              element={<UserRegistration />}
                            />
                            <Route
                              path="/template-builder"
                              element={<TemplateBuilder />}
                            />
                            <Route
                              path="/home/*"
                              element={
                                <UserAuth>
                                  <HomeDash />
                                </UserAuth>
                              }
                            />
                          </Routes>
                        </PageLoadBoundary>
                      </BrowserRouter>
                    </Fragment>
                  </PhoneNumberProvider>
                </AccountProvider>
              </UserProvider>
            </DialogProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
